import { useState } from 'react'
import DetailsIcon from '@/CustomIcons/DetailsIcon'
import { Card } from '../Card'
import { CardHeader } from '../CardHeader'
import ProcessorIcon from '@/CustomIcons/ProcessorIcon'
import { Field } from '../Field'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { TextInput } from '@/UIComponents2/TextInput'
import { Button } from '@/UIComponents2/Button'
import RefreshIcon from '@/CustomIcons/RefreshIcon'
import { TextField } from '@/UIComponents2/TextField'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import FlaskIcon from '@/CustomIcons/FlaskIcon'
import KeyIcon from '@/CustomIcons/KeyIcon'
import LightbulbIcon from '@/CustomIcons/LightbulbIcon'
import { getAudioEngineInstance } from '@/core/AudioEngine'
import { WebAudioEngine } from '@/core/AudioEngine/WebAudioEngine'
import { useAppContext } from '@/AppContext'
import { env } from '@/services/EnvironmentVariables'
import ElectronIPC from '@/services/ElectronIPC'
import { ControlButton } from '@/UIComponents2/ControlButton'
import FolderIcon from '@/CustomIcons/FolderIcon'
import Tooltip from '@/UIComponents2/Tooltip'
import useDeveloperOptionsActions from '@/functionalities/developer-options/useDeveloperOptionsActions'
import { LOG_LEVELS } from '@/services/Logger'
import { Switch } from '@/UIComponents2/Switch'
import { sessionViewModeState, useViewMode } from '@/pages/SessionPage/internal/shared/useViewMode'

export default function DeveloperSettings() {
    const ae = getAudioEngineInstance()
    const [appState, appDispatch] = useAppContext()
    const { getDeveloperOptions, updateDeveloperOptions } = useDeveloperOptionsActions()
    const viewMode = useViewMode()
    const [numTestVideos, setNumTestVideos] = useState(9)

    function renderEnv() {
        let val = ''
        for (const [k, v] of Object.entries(env)) {
            val += `${k}=${v}\n`
        }
        return val
    }

    const logLevelOptions = LOG_LEVELS.map(({ level, label }) => {
        return { value: level.toFixed(0), label: `${level}: ${label}` }
    })

    return (
        <div className="flex flex-col gap-[10px]">
            <Switch
                checked={appState.appSettings.developerOptions?.saveConfiguration}
                onChange={(val) => updateDeveloperOptions({ saveConfiguration: val })}
                label="Persist Developer Mode"
                className="self-end pr-4"
            />
            <Card className="flex flex-col gap-6">
                <CardHeader icon={DetailsIcon} title="Logging" />
                <div>
                    <div className="pb-2">Application Log Level</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4">
                        <SimpleSelect
                            value={appState.appSettings.developerOptions.logLevel ?? '300'}
                            onChange={(val) => updateDeveloperOptions({ logLevel: val })}
                            items={logLevelOptions}
                            widthMatching
                        />
                        <Checkbox
                            label="Save all logs to disk"
                            disabled={!env.IS_ELECTRON}
                            checked={appState.appSettings.developerOptions.enableAuditLogging}
                            onChange={(val) => updateDeveloperOptions({ enableAuditLogging: val })}
                        />
                    </div>
                </div>
                <div>
                    <div className="pb-2">Log Directory</div>
                    <div className="grid grid-cols-[1fr_min-content] items-center gap-2">
                        <TextInput value={env.LOGDIR ?? '(none)'} disabled />
                        <Tooltip text="Browse log directory">
                            <ControlButton
                                variant="secondary"
                                disabled={!env.IS_ELECTRON}
                                className="shadow-glow"
                                onClick={() => {
                                    ElectronIPC.openPath(env.LOGDIR)
                                }}
                            >
                                <FolderIcon />
                            </ControlButton>
                        </Tooltip>
                    </div>
                </div>
            </Card>
            <Card className="flex flex-col gap-6">
                <CardHeader icon={LightbulbIcon} title="Tools" />
                <div className="flex flex-wrap gap-3">
                    {env.IS_ELECTRON && <DevButton onClick={() => ElectronIPC.openDevTools()}>Dev Tools</DevButton>}
                    <DevButton
                        onClick={() =>
                            appDispatch({ type: 'update_app_state', value: { showScreenControlDiagnosticsPage: true } })
                        }
                    >
                        Screen Control Diagnostics
                    </DevButton>
                    <DevButton
                        onClick={() => appDispatch({ type: 'update_app_state', value: { showPerfStatsPage: true } })}
                    >
                        Performance Stats
                    </DevButton>
                    <DevButton
                        onClick={() =>
                            appDispatch({ type: 'update_app_state', value: { showStateTriggersDialog: true } })
                        }
                    >
                        State Triggers
                    </DevButton>
                    <TextInput
                        className="w-[220px]"
                        initialValue={numTestVideos.toFixed(0)}
                        onChange={(val) => setNumTestVideos(parseInt(val) || 1)}
                        rightControl={
                            <DevButton
                                onClick={() => {
                                    if (viewMode.galleryLayout.name !== 'test-grid') {
                                        sessionViewModeState.setGalleryLayout({
                                            name: 'test-grid',
                                            numVideos: numTestVideos,
                                            page: 0,
                                        })
                                    } else {
                                        sessionViewModeState.setGalleryLayout({
                                            name: 'grid',
                                            page: 0,
                                            reordering: false,
                                        })
                                    }
                                }}
                            >
                                {viewMode.galleryLayout.name === 'test-grid' ? 'Disable Test Grid' : 'Test Video Grid'}
                            </DevButton>
                        }
                    />
                </div>
            </Card>
            <Card className="flex flex-col gap-6">
                <CardHeader icon={FlaskIcon} title="Experimental" />
                <div className="px-[30px]">
                    <Checkbox
                        checked={appState.appSettings.developerOptions.disableBlurEffect ?? false}
                        onChange={(val) => updateDeveloperOptions({ disableBlurEffect: val })}
                        label="Disable Blur Effects"
                    />
                </div>
            </Card>
            <Card className="flex flex-col gap-6">
                <CardHeader icon={ProcessorIcon} title="Audio Engine" />
                <Field title="Implementation" className="items-center">
                    <span className="font-bold">{ae.implementation}</span>
                </Field>
                <Field title="Developer Settings" className="items-center">
                    <DevButton
                        onClick={() =>
                            appDispatch({ type: 'update_app_state', value: { showAudioEngineDevSettingsPage: true } })
                        }
                    >
                        Open
                    </DevButton>
                </Field>
                <Field title="Test Tools" className="items-center">
                    <div className="flex gap-3">
                        {ae instanceof WebAudioEngine ? (
                            <DevButton onClick={() => ae.resetAudioContext()}>
                                <RefreshIcon className="pr-1" /> Reset
                            </DevButton>
                        ) : null}
                        <DevButton onClick={() => ae.testCrash()}>💥 Crash</DevButton>
                    </div>
                </Field>
            </Card>
            <Card className="flex flex-col gap-6">
                <CardHeader icon={KeyIcon} title="Environment Variables" />
                <TextField value={renderEnv()} disabled rows={8} />
            </Card>
        </div>
    )
}

function DevButton({ children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <Button variant="secondary" className="py-2 px-3" {...rest}>
            {children}
        </Button>
    )
}
